import styled from "@emotion/styled";
import { AnimatePresence } from "framer-motion";
import React, { useMemo, useState } from "react";
import { useTravelTimesEstimator } from "../../hooks/useTravelTimesEstimator";
import { Geo } from "../../messages";
import { STRINGS } from "../../strings";
import { Visit } from "../../system/AppState/AppVisitsState";
import { translate } from "../../translate";
import { AppButton } from "../Generic/AppButton";
import AppModal from "../Generic/AppModal/AppModal";
import { AppOverlay } from "../Generic/AppOverlay";
import { If } from "../Generic/If";
import { isDefined } from "../Generic/IsDefined";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import Scaffold from "../Scaffold/Scaffold";
import useModal from "../useModal";
import { SlideContainer } from "./Slides/Slide";
import { Slide3CaseInfo } from "./Slides/Slide3CaseInfo";
import { Slide5FinalConfirmation } from "./Slides/Slide5FinalConfirmation";
import { Slide6Congratulations } from "./Slides/Slide6Congratulations";
import { caseSlides, slides } from "./Slides/slides";

interface Props {
  visit: Visit.Available;
  isConfirmed: boolean;
  allCaregiversConfirmed: boolean;
  isReviewOnly: boolean;
  caregiverGeoAddress: Geo;
  onClickGoBack: () => void;
  onComplete: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  onClickLocation: () => void;
}

type Slide = (typeof slides)[number];

export const VisitPendingConfirmationScreen = (props: Props) => {
  const travelTimesQuery = useTravelTimesEstimator({
    source: props.caregiverGeoAddress,
    destination: props.visit.publicVisitBroadcastDetails.location.location,
  });

  const modal = useModal();

  const [slideIndex, setSlideIndex] = useState(0);
  const nextSlideIndex = slideIndex + 1;

  const [isCaseConfirmed, setIsCaseConfirmed] = useState(false);

  const { footerRef, footerHeight } = useFooterHeight(slideIndex);

  const userSlides = useMemo(() => {
    const emptyCaseSlideFilter = (slide: Slide) =>
      slide !== Slide3CaseInfo || isCaseInfoNotEmpty(props.visit);
    return props.isReviewOnly
      ? caseSlides.filter(emptyCaseSlideFilter)
      : slides.filter(emptyCaseSlideFilter);
  }, [props.visit, props.isReviewOnly]);

  const currentSlide = userSlides[slideIndex];
  if (!isDefined(currentSlide)) {
    return null;
  }

  const slideStepsCount = props.isReviewOnly ? userSlides.length : userSlides.length - 2;
  const isLastSlide = props.isReviewOnly
    ? nextSlideIndex === userSlides.length
    : currentSlide === Slide6Congratulations;
  const isFinalConfirmationStep = currentSlide === Slide5FinalConfirmation;

  if (isFinalConfirmationStep && props.isConfirmed) {
    setSlideIndex((value) => value + 1);
  }

  const nextSlide = () => {
    if (isFinalConfirmationStep) {
      props.onConfirm();
    } else if (nextSlideIndex < userSlides.length) {
      setSlideIndex((value) => value + 1);
    }
  };

  const showDeclineModalWindow = () =>
    modal.setModalAsPrompt({
      title: translate(STRINGS.CANCEL_VISIT),
      message: translate(STRINGS.ARE_YOU_SURE_YOU_WANT_TO_CANCEL_THIS_VISIT),
      withoutClose: true,
      actions: [
        {
          key: "yes",
          variant: "soft",
          color: "black",
          children: translate(STRINGS.YES),
          onClick: () => props.onCancel(),
        },
        {
          key: "no",
          variant: "link",
          color: "black",
          children: translate(STRINGS.I_HAVE_CHANGED_MY_MIND),
          onClick: () => modal.setModalAsClosed(),
        },
      ],
    });

  const footerWithNextButtons = (
    <BottomSheetFooter ref={footerRef}>
      <AppButton
        color="primary"
        variant="filled"
        onClick={nextSlide}
        disabled={isFinalConfirmationStep && !isCaseConfirmed}
      >
        {translate(STRINGS.NEXT)}
      </AppButton>
      <If condition={!props.isReviewOnly}>
        <AppButton color="black" variant="link" onClick={showDeclineModalWindow}>
          {translate(STRINGS.I_DONT_WANT_THIS_CASE)}
        </AppButton>
      </If>
    </BottomSheetFooter>
  );

  const completeHandler = props.isReviewOnly ? props.onClickGoBack : props.onComplete;
  const goBackHandler = isLastSlide ? completeHandler : props.onClickGoBack;

  const footerComplete = (
    <BottomSheetFooter ref={footerRef}>
      <AppButton onClick={completeHandler} color="primary" variant="filled">
        {translate(STRINGS.THANKS)}
      </AppButton>
    </BottomSheetFooter>
  );

  const footer = !isLastSlide ? footerWithNextButtons : footerComplete;

  const { patientFirstName } = props.visit.publicVisitBroadcastDetails;
  const isBigScreen = window.innerHeight > 700;

  return (
    <VisitPendingConfirmationScreenScaffold
      title={<ScaffoldTitle label={patientFirstName} />}
      onClickGoBack={goBackHandler}
      className="VisitPendingConfirmationScreen"
      footer={footer}
    >
      <AnimatePresence initial={false} exitBeforeEnter={true}>
        <SlideContainer
          style={{ paddingBottom: footerHeight }}
          key={slideIndex}
          initial={{ translateX: 1000 }}
          animate={{ translateX: 0 }}
          exit={{ translateX: -1000, transition: { duration: 0.1 } }}
          transition={{ duration: 0.2 }}
        >
          {currentSlide({
            slideIndex: slideIndex,
            slideStepsCount: slideStepsCount,
            showSteps: !props.isReviewOnly,
            visit: props.visit,
            isBigScreen: isBigScreen,
            allCaregiversConfirmed: props.allCaregiversConfirmed,
            travelTimesQuery: travelTimesQuery,
            onSignatureConfirmed: setIsCaseConfirmed,
            onClickLocation: props.onClickLocation,
          })}
        </SlideContainer>
      </AnimatePresence>
      <AppOverlay>
        <AppModal {...modal.modalProps} />
      </AppOverlay>
    </VisitPendingConfirmationScreenScaffold>
  );
};

function isCaseInfoNotEmpty(visit: Visit.Available) {
  const { patientRequests, patientTasks, patientMedicalNotes, patientAllergies } =
    visit.publicVisitBroadcastDetails;

  const caseInfoIsNotEmpty =
    patientRequests.length > 0 ||
    patientTasks.length > 0 ||
    (isDefined(patientMedicalNotes) && patientMedicalNotes.length > 0) ||
    patientAllergies.length > 0;
  return caseInfoIsNotEmpty;
}

const VisitPendingConfirmationScreenScaffold = styled(Scaffold)`
  background-color: white;

  .__body {
    display: flex;
    flex-direction: column;
  }
`;

const BottomSheetFooter = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  align-self: stretch;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
  background-color: white;

  box-shadow: 0px -8px 24px rgba(40, 56, 71, 0.06);
  border-radius: 24px 24px 0px 0px;

  .AppButton {
    font-size: 16px;
    margin: 0;
  }

  & > * + * {
    margin-top: 12px;
  }
`;

const useFooterHeight = (slideIndex: number) => {
  const footerRef = React.useRef<HTMLDivElement>(null);

  const [footerHeight, setFooterHeight] = React.useState(footerRef.current?.clientHeight ?? 0);

  React.useEffect(() => {
    const { current: footerElem } = footerRef;
    const footerClientHeight = footerElem?.clientHeight ?? 0;
    setFooterHeight(footerClientHeight);
  }, [footerRef, slideIndex]);

  return { footerRef, footerHeight };
};
